import * as React from 'react';
import { jsx, css, keyframes } from '@emotion/react';
import { setTime, wait } from 'Src/me/pages/guest/proposals/new/utils';
import { mediaQueries, autoResponsiveClamp } from 'Src/me/pages/guest/proposals/new/React/viewHelpers';
import type { NonInteractiveContinueHandlerType, FeaturePropsType } from '../../../../../chat/views/types';
import type { RenderAdvisorMessagePropsType } from '../../../../../chat/views/AdvisorMessage/index';
import { scrollToBottomOfChat } from '../../../../../chat/functional/scrollToBottomOfChat';
import { Avatar } from './components/Avatar';
import { AdvisorChatBubble } from './components/AdvisorChatBubble';

const { tablet, mobile } = mediaQueries();

export type AdvisorMessagePropsType = FeaturePropsType<RenderAdvisorMessagePropsType & {
  isFirstElement: boolean;
  isWithAvatar: boolean;
}, NonInteractiveContinueHandlerType>

export const AdvisorMessage = (props: AdvisorMessagePropsType) => {
  const {
    keyName,
    doesNotScroll,
    isInstant = false,
    continueHandler,
    avatarName = 'ヌリカエ',
    messageElement,
    overrideMessageElement,
    balloonContents,
    loadingTime = 1,
    delayTime = 1,
    isFirstElement,
    isWithAvatar,
  } = props;
  const [isLoading, setIsLoading] = React.useState(!isInstant);
  const [message, setMessage] = React.useState<React.ReactNode>(messageElement);

  React.useEffect(() => {
    (async () => {
      if (!isInstant) {
        await wait(loadingTime);

        setIsLoading(false);

        await wait(delayTime);
      }

      if (overrideMessageElement) {
        await wait(4);

        setIsLoading(true);
        setMessage(overrideMessageElement);

        await wait(1);

        setIsLoading(false);

        await wait(1);
      }

      continueHandler({
        selfDomReferences: [],
      }); // 次に進む
    })();
  }, [continueHandler, isInstant, overrideMessageElement, loadingTime, delayTime]);

  React.useEffect(() => {
    if (!doesNotScroll) scrollToBottomOfChat();
  }, [doesNotScroll]);

  return (
    <div
      css={css`
        display: grid;
        grid-template-columns: 80px 1fr;
        gap: 0 16px;
        padding-top: ${isWithAvatar ? '30px' : '10px'};

        ${mobile(({ clamp }) => css`
          grid-template-columns: ${clamp('40px', '58px')} 1fr;
          gap: 0 ${clamp('10px', '16px')};
          padding-left: 12px;
          padding-right: 12px;
        `)}

        ${balloonContents && css`
          padding-bottom: 46px;

          ${mobile(({ setAutomaticDesignBasedSize }) => css`
            padding-bottom: ${setAutomaticDesignBasedSize(46, 46)};
          `)}
        `}

        ${isFirstElement && css`
          padding-top: 0;
        `}
      `}
    >
      <div
        css={css`
          width: 80px;

          ${mobile(({ clamp }) => css`
            width: ${clamp('40px', '58px')};
          `)}
        `}
      >
        <Avatar {...props} isVisibleAvatar={isWithAvatar} />
      </div>
      <div
        css={css`
          padding-right: 10px;
          position: relative;
        `}
      >
        <div
          css={css`
            margin-bottom: 8px;

            ${mobile(({ clamp }) => css`
              margin-bottom: ${clamp('2px', '6px')};
            `)}

            ${!isWithAvatar && css`
              display: none;
            `}
          `}
        >
          <p
            css={css`
              color: #000;
              font-size: 15px;

              ${mobile(({ clamp }) => css`
                font-size: ${clamp('10px', '13px')};
              `)}
            `}
          >
            {avatarName}
          </p>
        </div>
        <AdvisorChatBubble {...props} message={message} isLoading={isLoading} />
        {balloonContents && (
          <div
            css={css`
              color: #136f84;
              font-size: 14px;
              text-align: center;
              white-space: nowrap;
              min-width: 50px;
              padding: 6px 12px;
              background-color: #aef1ff;
              border-radius: 6px;
              filter: drop-shadow(0 1px 4px rgb(0 0 0 / 0.6));
              position: absolute;
              top: calc(100% - 6px);
              left: 50%;
              transform: translateX(-50%);
              opacity: 0;
              visibility: hidden;
              animation: ${setTime(0.4)}s ${setTime(3)}s forwards ${keyframes`
                from {
                  opacity: 0;
                  visibility: hidden;
                  top: calc(100% - 6px) ;
                }

                to {
                  opacity: 1;
                  visibility: visible;
                  top: calc(100% + 4px) ;
                }
              `};
              pointer-events: none;

              ${tablet(() => css`
                transform: translate3d(-50%, 0, 0);
              `)}

              ${mobile(({ setAutomaticDesignBasedSize }) => css`
                font-size: ${setAutomaticDesignBasedSize(14, 14)};
                min-width: ${setAutomaticDesignBasedSize(50, 50)};
                padding: ${setAutomaticDesignBasedSize(6, 6)} ${setAutomaticDesignBasedSize(12, 12)};
              `)}

              &::before {
                content: "";
                width: 16px;
                height: 10px;
                clip-path: polygon(50% 0%, 0% 100%, 100% 100%);
                background-color: #aef1ff;
                position: absolute;
                bottom: calc(100% - 1px);
                left: 85%;
                transform: translateX(-50%);

                ${mobile(({ setAutomaticDesignBasedSize }) => css`
                  width: ${setAutomaticDesignBasedSize(16, 16)};
                  height: ${setAutomaticDesignBasedSize(10, 10)};
                `)}
              }
            `}
          >
            {balloonContents}
          </div>
        )}
      </div>
    </div>
  );
};
